import React from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, CATEGORIES } from "../lib/api";
import SEO from "../components/site/SEO";

export default function Contact() {
    const [params] = useSearchParams();
    const initialType = params.get("type") || "automobile";

    const [form, setForm] = React.useState({
        name: "",
        email: "",
        phone: "",
        prestation_type: initialType,
        event_date: "",
        message: "",
    });
    const [submitting, setSubmitting] = React.useState(false);
    const [sent, setSent] = React.useState(false);

    const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...form };
            if (!payload.phone) delete payload.phone;
            if (!payload.event_date) delete payload.event_date;
            await api.post("/quote-requests", payload);
            setSent(true);
            toast.success("Demande envoyée ! Je reviens vers vous très vite.");
            setForm({
                name: "",
                email: "",
                phone: "",
                prestation_type: initialType,
                event_date: "",
                message: "",
            });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string"
                ? detail
                : "Erreur lors de l'envoi. Réessayez.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="page-contact" className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
            <SEO title="Contact & devis" description="Parlons de votre projet. Demande de devis sous 48h pour photo automobile, mariage, baptême ou portrait." />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">— Contact</p>
                    <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.95]">
                        Parlons de<br /><em className="italic text-[#c5a059]">votre projet.</em>
                    </h1>
                    <p className="text-zinc-400 mt-8 leading-relaxed max-w-md">
                        Remplissez ce formulaire avec quelques détails — date, lieu,
                        type de prestation — et je reviens vers vous sous 48h
                        avec un devis personnalisé.
                    </p>

                    <div className="mt-12 space-y-4 text-zinc-300">
                        <div>
                            <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">Email</p>
                            <p className="mt-1">paulmoisson76@gmail.com</p>
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">Instagram</p>
                            <p className="mt-1">@paul.mv07</p>
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">Zone</p>
                            <p className="mt-1">France — déplacements possibles</p>
                        </div>
                    </div>
                </div>

                <form data-testid="contact-form" onSubmit={submit} className="bg-[#0f0f0f] border border-white/10 p-8 md:p-10 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Field label="Nom" required>
                            <input
                                data-testid="contact-name"
                                required
                                value={form.name}
                                onChange={setField("name")}
                                className="input"
                            />
                        </Field>
                        <Field label="Email" required>
                            <input
                                data-testid="contact-email"
                                type="email"
                                required
                                value={form.email}
                                onChange={setField("email")}
                                className="input"
                            />
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Field label="Téléphone">
                            <input
                                data-testid="contact-phone"
                                value={form.phone}
                                onChange={setField("phone")}
                                className="input"
                            />
                        </Field>
                        <Field label="Date prévue">
                            <input
                                data-testid="contact-date"
                                type="date"
                                value={form.event_date}
                                onChange={setField("event_date")}
                                className="input"
                            />
                        </Field>
                    </div>

                    <Field label="Type de prestation" required>
                        <select
                            data-testid="contact-type"
                            value={form.prestation_type}
                            onChange={setField("prestation_type")}
                            className="input"
                        >
                            {CATEGORIES.map((c) => (
                                <option key={c.key} value={c.key}>{c.label}</option>
                            ))}
                        </select>
                    </Field>

                    <Field label="Votre message" required>
                        <textarea
                            data-testid="contact-message"
                            required
                            rows={5}
                            value={form.message}
                            onChange={setField("message")}
                            className="input resize-none"
                            placeholder="Parlez-moi de votre projet, du lieu, de l'ambiance souhaitée…"
                        />
                    </Field>

                    <button
                        data-testid="contact-submit"
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-[#c5a059] text-black font-medium py-4 uppercase tracking-[0.2em] text-sm hover:bg-[#dcc082] transition-colors disabled:opacity-50"
                    >
                        {submitting ? "Envoi…" : sent ? "Envoyé — envoyer une autre" : "Envoyer la demande"}
                    </button>

                    <style>{`
                        .input {
                            width: 100%;
                            background: transparent;
                            border: 1px solid rgba(255,255,255,0.15);
                            color: #f8f8f8;
                            padding: 0.75rem 1rem;
                            font-size: 0.95rem;
                            outline: none;
                            transition: border-color 0.2s;
                        }
                        .input:focus { border-color: #c5a059; }
                        .input::placeholder { color: #6b6b6b; }
                        select.input option { background: #0f0f0f; color: #f8f8f8; }
                    `}</style>
                </form>
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-[0.22em] text-zinc-400">
                {label} {required && <span className="text-[#c5a059]">*</span>}
            </span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
